import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { isApiErrorResponse } from 'shared';
import { media } from 'context';
import { Layout, CarouselArrow, Link, PillButton, Text, ToggleControl } from 'ds';
import { formatUsd } from 'helpers';
import { wrapRequest } from 'helpers/requests';
import { showSnackbar } from 'helpers/snackbar';
import { formatConsumablesOrderLockCopyPhrase } from 'ux/Tenant/PantryRestocking/utils';
import {
  createConsumablesOrderItem,
  getConsumablesOrders,
  getConsumablesPickerDraft,
  getConsumablesProductSubscriptions,
  saveConsumablesPickerDraft,
  verifyConsumablesOrderBudget,
  type ConsumablesPickerDraftPayload,
  subscribeToProduct
} from 'ux/Tenant/PantryRestocking/requests';
import { PANTRY_ORDERS_PATH } from 'routes';
import type { ConsumablesOrder } from 'shared/types';
import { actions as consumablesActions } from 'store/Consumables';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectHasTenantAdminType } from 'store/v2/User/selectors';

import type { ConsumablesProductPickerMetadata, ConsumablesProductPickerOption } from '../types';

import AIAssistantConsumablesPickerConfirmed, {
  type ConsumablesPickerConfirmedLineItem
} from './AIAssistantConsumablesPickerConfirmed';
import AIAssistantConsumablesPickerProductCard from './AIAssistantConsumablesPickerProductCard';

const MAX_QTY = 50;
/** Keep in sync with consumables confirm transition in `AIAssistant.scss`. */
const CONSUMABLES_PICKER_CONFIRM_TRANSITION_MS = 320;

type ApplyMode = 'recurring' | 'one_time';
type ConsumablesPickerViewPhase = 'picker' | 'pickerExiting' | 'confirmed';

export type ConsumablesPickerConfirmDetails = {
  applyMode: ApplyMode;
  nextOrderLockPhrase: string | null;
  nextOrderEditPath: string | null;
};

export interface AIAssistantConsumablesProductPickerProps {
  payload: ConsumablesProductPickerMetadata;
  conversationId: number;
  messageId: number;
  onConfirm?: (details: ConsumablesPickerConfirmDetails) => void;
  onRestoreConfirmDetails?: (details: ConsumablesPickerConfirmDetails | null) => void;
}

type ConfirmedSnapshot = {
  applyMode: ApplyMode;
  lineItems: ConsumablesPickerConfirmedLineItem[];
  totalCents: number;
  budgetCheckOrderId: number | null;
};

type CarouselScrollState = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
};

const EMPTY_CAROUSEL_SCROLL_STATE: CarouselScrollState = {
  canScrollLeft: false,
  canScrollRight: false
};

/** Ignore subpixel / snap jitter at scroll edges (px). */
const CAROUSEL_SCROLL_EDGE_SLACK_PX = 2;

const buildInitialSelection = (groups: ConsumablesProductPickerMetadata['selection_groups']) => {
  const selected: Record<string, number[]> = {};
  const quantities: Record<string, number> = {};
  (groups || []).forEach(g => {
    if (!g?.id) return;
    const options = g.options || [];
    selected[g.id] = options.length === 1 && options[0]?.product_id != null ? [options[0].product_id] : [];
    (g.options || []).forEach(opt => {
      if (opt?.product_id == null) return;
      quantities[`${g.id}:${opt.product_id}`] = opt.default_quantity ?? 1;
    });
  });
  return { selectedByGroup: selected, quantities };
};

/** Drop malformed groups from API so the widget never throws mid-render. */
const normalizePickerGroups = (
  raw: ConsumablesProductPickerMetadata['selection_groups']
): ConsumablesProductPickerMetadata['selection_groups'] =>
  (raw || [])
    .filter(g => g != null && typeof g.id === 'string' && g.id.length > 0 && Array.isArray(g.options))
    .map(g => ({
      ...g,
      options: (g.options || []).filter(
        (opt): opt is NonNullable<typeof opt> =>
          opt != null && typeof opt === 'object' && opt.product_id != null && Number.isFinite(Number(opt.product_id))
      )
    }))
    .filter(g => g.options.length > 0);

const toConsumablesPickerPayloadFingerprint = (
  version: number,
  groups: ConsumablesProductPickerMetadata['selection_groups']
): string => {
  const normalizedGroups = (groups || []).map(group => ({
    id: group.id,
    options: (group.options || []).map(option => option.product_id).sort((a, b) => a - b)
  }));
  return JSON.stringify({ version, groups: normalizedGroups });
};

const isValidPersistedApplyMode = (value: unknown): value is ApplyMode => value === 'recurring' || value === 'one_time';

const isValidPersistedViewPhase = (value: unknown): value is ConsumablesPickerViewPhase =>
  value === 'picker' || value === 'pickerExiting' || value === 'confirmed';

const draftSelectionsOverlapCurrentOffer = (
  state: ConsumablesPickerDraftPayload,
  normalizedGroups: ConsumablesProductPickerMetadata['selection_groups']
): boolean => {
  const offerIds = new Set(
    normalizedGroups.flatMap(g => (g.options || []).map(o => Number(o.product_id)).filter(id => Number.isFinite(id)))
  );
  const byGroup = state.selectedByGroup ?? {};
  for (const ids of Object.values(byGroup)) {
    if (!Array.isArray(ids)) continue;
    for (const rawId of ids) {
      const id = Number(rawId);
      if (Number.isFinite(id) && offerIds.has(id)) return true;
    }
  }
  return false;
};

const shouldHydrateDraftDespiteFingerprintMismatch = (
  state: ConsumablesPickerDraftPayload | undefined,
  normalizedGroups: ConsumablesProductPickerMetadata['selection_groups']
): boolean => {
  if (!state) return false;
  if (state.viewPhase === 'confirmed' && state.confirmedSnapshot != null) return true;
  return draftSelectionsOverlapCurrentOffer(state, normalizedGroups);
};

const AIAssistantConsumablesProductPicker: React.FC<AIAssistantConsumablesProductPickerProps> = ({
  payload,
  conversationId,
  messageId,
  onConfirm,
  onRestoreConfirmDetails
}) => {
  const dispatch = useAppDispatch();
  const { isMobile } = useContext(media);
  const isTenantAdmin = useAppSelector(selectHasTenantAdminType);
  const groups = useMemo(() => normalizePickerGroups(payload.selection_groups), [payload.selection_groups]);
  const [resolvedOrderId, setResolvedOrderId] = useState<number | null>(payload.upcoming_order_id ?? null);
  const [applyMode, setApplyMode] = useState<ApplyMode>('recurring');
  const [orderQtyByProductId, setOrderQtyByProductId] = useState<Record<number, number>>({});
  const [subscriptionQtyByProductId, setSubscriptionQtyByProductId] = useState<Record<number, number>>({});
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const [, setBudgetNoConfig] = useState(false);
  const [budgetCheckPending, setBudgetCheckPending] = useState(false);
  const carouselRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [carouselScrollStateByGroup, setCarouselScrollStateByGroup] = useState<Record<string, CarouselScrollState>>({});
  const groupIds = groups.map(group => group.id).join(',');

  const [{ selectedByGroup, quantities }, setPickerState] = useState(() => buildInitialSelection(groups));

  const setSelectedByGroup = (updater: (prev: Record<string, number[]>) => Record<string, number[]>) => {
    setPickerState(prev => ({ ...prev, selectedByGroup: updater(prev.selectedByGroup) }));
  };

  const setQuantities = (updater: (prev: Record<string, number>) => Record<string, number>) => {
    setPickerState(prev => ({ ...prev, quantities: updater(prev.quantities) }));
  };

  const qtyKey = (groupId: string, productId: number) => `${groupId}:${productId}`;

  const updateCarouselScrollState = (groupId: string) => {
    const carousel = carouselRefs.current[groupId];
    if (!carousel) return;

    const scrollLeft = carousel.scrollLeft;
    const maxScrollLeft = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    const nextState = {
      canScrollLeft: maxScrollLeft > 0 && scrollLeft > CAROUSEL_SCROLL_EDGE_SLACK_PX,
      canScrollRight: maxScrollLeft > 0 && scrollLeft < maxScrollLeft - CAROUSEL_SCROLL_EDGE_SLACK_PX
    };

    setCarouselScrollStateByGroup(prev => {
      const current = prev[groupId];
      if (current?.canScrollLeft === nextState.canScrollLeft && current?.canScrollRight === nextState.canScrollRight) {
        return prev;
      }
      return { ...prev, [groupId]: nextState };
    });
  };

  const handleCarouselArrowClick = (groupId: string, direction: 'left' | 'right') => {
    const carousel = carouselRefs.current[groupId];
    if (!carousel) return;

    const scrollDistance = Math.max(160, carousel.clientWidth - 48);
    carousel.scrollBy({ left: direction === 'left' ? -scrollDistance : scrollDistance, behavior: 'smooth' });
    window.setTimeout(() => updateCarouselScrollState(groupId), 300);
  };

  const pickerSubtotalCents = groups.reduce((sum, group) => {
    const selectedIds = selectedByGroup[group.id] || [];
    if (selectedIds.length === 0) return sum;

    const selectedTotal = selectedIds.reduce((groupSum, selectedId) => {
      const opt = (group.options || []).find(o => o.product_id === selectedId);
      if (!opt) return groupSum;
      const q = quantities[qtyKey(group.id, selectedId)] ?? 1;
      const unitCents = Number(opt.unit_amount_cents);
      if (!Number.isFinite(unitCents)) return groupSum;
      return groupSum + q * unitCents;
    }, 0);

    return sum + selectedTotal;
  }, 0);

  const hasAnySelection = Object.values(selectedByGroup).some(productIds => productIds.length > 0);

  const canAddToOrder =
    resolvedOrderId != null && hasAnySelection && pickerSubtotalCents > 0 && !budgetError && !budgetCheckPending;

  const [submitting, setSubmitting] = useState(false);
  const [confirmedSnapshot, setConfirmedSnapshot] = useState<ConfirmedSnapshot | null>(null);
  const [viewPhase, setViewPhase] = useState<ConsumablesPickerViewPhase>('picker');
  const [confirmDetails, setConfirmDetails] = useState<ConsumablesPickerConfirmDetails | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const persistDebounceTimerRef = useRef<number | null>(null);
  const hasLoadedDraftRef = useRef(false);
  const payloadFingerprint = toConsumablesPickerPayloadFingerprint(payload.version, groups);

  useEffect(() => {
    if (viewPhase !== 'pickerExiting') return;
    const timerId = window.setTimeout(() => {
      setViewPhase('confirmed');
    }, CONSUMABLES_PICKER_CONFIRM_TRANSITION_MS);
    return () => window.clearTimeout(timerId);
  }, [viewPhase]);

  useEffect(() => {
    let cancelled = false;

    const hydrateDraft = async () => {
      try {
        const response = await getConsumablesPickerDraft(conversationId, messageId);
        if (cancelled) return;

        const draft = response.data.draft;
        const fpMatches = draft != null && draft.payload_fingerprint === payloadFingerprint;
        const statePreview =
          draft?.state != null && typeof draft.state === 'object'
            ? (draft.state as ConsumablesPickerDraftPayload)
            : undefined;
        const allowFingerprintMismatch =
          draft != null &&
          typeof draft.state === 'object' &&
          shouldHydrateDraftDespiteFingerprintMismatch(statePreview, groups);

        if (!draft || typeof draft.state !== 'object') {
          onRestoreConfirmDetails?.(null);
          setIsHydrated(true);
          return;
        }

        if (!fpMatches && !allowFingerprintMismatch) {
          onRestoreConfirmDetails?.(null);
          setIsHydrated(true);
          return;
        }

        const state = draft.state as ConsumablesPickerDraftPayload;
        if (state.selectedByGroup && state.quantities) {
          setPickerState({
            selectedByGroup: state.selectedByGroup,
            quantities: state.quantities
          });
        }

        if (isValidPersistedApplyMode(state.applyMode)) {
          setApplyMode(state.applyMode);
        }

        if (state.confirmedSnapshot) {
          const snap = state.confirmedSnapshot as ConfirmedSnapshot;
          setConfirmedSnapshot({
            ...snap,
            budgetCheckOrderId: snap.budgetCheckOrderId ?? payload.upcoming_order_id ?? null
          });
        }

        if (isValidPersistedViewPhase(state.viewPhase)) {
          // Never replay the transient exit animation after reload; jump to the
          // stable confirmed view to avoid a picker flash on hydration.
          const restoredViewPhase = state.viewPhase === 'pickerExiting' ? 'confirmed' : state.viewPhase;
          setViewPhase(restoredViewPhase);
        }

        if (state.confirmDetails) {
          setConfirmDetails(state.confirmDetails);
          onRestoreConfirmDetails?.(state.confirmDetails);
        } else {
          setConfirmDetails(null);
          onRestoreConfirmDetails?.(null);
        }
      } catch {
        setConfirmDetails(null);
        onRestoreConfirmDetails?.(null);
      } finally {
        if (!cancelled) {
          hasLoadedDraftRef.current = true;
          setIsHydrated(true);
        }
      }
    };

    void hydrateDraft();

    return () => {
      cancelled = true;
    };
  }, [conversationId, messageId, payloadFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;

    const loadCurrentQuantities = async () => {
      try {
        const [ordersResponse, subscriptionsResponse] = await Promise.all([
          getConsumablesOrders(),
          getConsumablesProductSubscriptions()
        ]);
        if (cancelled) return;

        const order =
          ordersResponse.data.find(orderItem => orderItem.id === resolvedOrderId) ||
          ordersResponse.data.find(orderItem => orderItem.status === 'open');
        if (order?.id != null) setResolvedOrderId(order.id);
        const nextOrderMap = (order?.consumables_order_items || []).reduce<Record<number, number>>((acc, item) => {
          const productId = item.consumables_product?.id;
          if (productId == null) return acc;
          acc[productId] = item.quantity ?? 0;
          return acc;
        }, {});
        setOrderQtyByProductId(nextOrderMap);

        const subscriptionMap = subscriptionsResponse.data.reduce<Record<number, number>>((acc, sub) => {
          const productId = sub.consumables_product?.id;
          if (productId == null) return acc;
          acc[productId] = sub.order_quantity ?? 0;
          return acc;
        }, {});
        setSubscriptionQtyByProductId(subscriptionMap);
      } catch {
        // Keep picker usable even if quantity lookups fail.
      }
    };

    void loadCurrentQuantities();
    return () => {
      cancelled = true;
    };
  }, [resolvedOrderId]);

  useEffect(() => {
    groups.forEach(group => updateCarouselScrollState(group.id));

    const handleResize = () => {
      groups.forEach(group => updateCarouselScrollState(group.id));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [groupIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isHydrated || !hasLoadedDraftRef.current) return;

    if (persistDebounceTimerRef.current !== null) {
      window.clearTimeout(persistDebounceTimerRef.current);
    }

    persistDebounceTimerRef.current = window.setTimeout(() => {
      const stateToPersist: ConsumablesPickerDraftPayload = {
        selectedByGroup,
        quantities,
        applyMode,
        viewPhase,
        confirmedSnapshot,
        confirmDetails
      };
      void wrapRequest(saveConsumablesPickerDraft(conversationId, messageId, payloadFingerprint, stateToPersist), {
        showSuccessMessage: false,
        showErrorMessage: false
      });
    }, 200);

    return () => {
      if (persistDebounceTimerRef.current !== null) {
        window.clearTimeout(persistDebounceTimerRef.current);
      }
    };
  }, [
    conversationId,
    messageId,
    payloadFingerprint,
    selectedByGroup,
    quantities,
    applyMode,
    viewPhase,
    confirmedSnapshot,
    confirmDetails,
    isHydrated
  ]);

  useEffect(() => {
    if (!resolvedOrderId || !hasAnySelection) {
      setBudgetError(null);
      setBudgetNoConfig(false);
      setBudgetCheckPending(false);
      return;
    }

    if (isTenantAdmin) {
      setBudgetError(null);
      setBudgetNoConfig(false);
      setBudgetCheckPending(false);
      return;
    }

    const lines = groups.flatMap(group => {
      const productIds = selectedByGroup[group.id] || [];
      return productIds.map(productId => ({
        product_id: productId,
        quantity: quantities[qtyKey(group.id, productId)] ?? 1
      }));
    });
    if (lines.length === 0) {
      setBudgetError(null);
      setBudgetNoConfig(false);
      setBudgetCheckPending(false);
      return;
    }

    let cancelled = false;
    setBudgetCheckPending(true);
    verifyConsumablesOrderBudget(resolvedOrderId, lines, applyMode)
      .then(response => {
        if (cancelled) return;
        const data = response.data;
        if (data.valid) {
          setBudgetError(null);
          setBudgetNoConfig(false);
          return;
        }
        setBudgetError(data.error || 'Unable to verify consumables budget.');
        setBudgetNoConfig(Boolean(data.no_budget_configured));
      })
      .catch(error => {
        if (cancelled) return;
        const message = isApiErrorResponse(error) ? error.message : 'Unable to verify consumables budget.';
        setBudgetError(message);
        setBudgetNoConfig(false);
      })
      .finally(() => {
        if (!cancelled) setBudgetCheckPending(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyMode, groups, hasAnySelection, isTenantAdmin, quantities, selectedByGroup, resolvedOrderId]);
  const handleAddToOrder = async () => {
    if (!canAddToOrder || submitting) return;

    const submitApplyMode = applyMode;

    const lines = groups.flatMap(group => {
      const productIds = selectedByGroup[group.id] || [];
      return productIds.map(productId => {
        const q = quantities[qtyKey(group.id, productId)] ?? 1;
        return { productId, quantity: q };
      });
    });

    if (lines.length === 0) return;

    setSubmitting(true);
    try {
      let lastResponse: Awaited<ReturnType<typeof createConsumablesOrderItem>> | undefined;
      for (const line of lines) {
        const currentSubscriptionQty = subscriptionQtyByProductId[line.productId] ?? 0;
        const request =
          applyMode === 'recurring'
            ? subscribeToProduct(line.productId, currentSubscriptionQty + line.quantity)
            : createConsumablesOrderItem(line.productId, line.quantity);
        lastResponse = await wrapRequest(request, {
          showSuccessMessage: false,
          showErrorMessage: false
        });
      }
      let orderAfterSubmit: ConsumablesOrder | undefined;
      if (lastResponse?.data) {
        const orders = lastResponse.data.consumables_orders as ConsumablesOrder[] | undefined;
        orderAfterSubmit =
          orders?.find(orderItem => orderItem.id === resolvedOrderId) ??
          orders?.find(orderItem => orderItem.status === 'open');
        dispatch(consumablesActions.setConsumablesOrders(lastResponse.data.consumables_orders));
        dispatch(
          consumablesActions.setConsumablesProductSubscriptions(lastResponse.data.consumables_product_subscriptions)
        );
        const nextOrderMap = (orderAfterSubmit?.consumables_order_items || []).reduce(
          (acc: Record<number, number>, item: { consumables_product?: { id?: number }; quantity?: number }) => {
            const productId = item.consumables_product?.id;
            if (productId == null) return acc;
            acc[productId] = item.quantity ?? 0;
            return acc;
          },
          {} as Record<number, number>
        );
        setOrderQtyByProductId(nextOrderMap);

        const subscriptionMap = (lastResponse.data.consumables_product_subscriptions || []).reduce(
          (acc: Record<number, number>, sub: { consumables_product?: { id?: number }; order_quantity?: number }) => {
            const productId = sub.consumables_product?.id;
            if (productId == null) return acc;
            acc[productId] = sub.order_quantity ?? 0;
            return acc;
          },
          {} as Record<number, number>
        );
        setSubscriptionQtyByProductId(subscriptionMap);
      }

      let nextOrderLockPhrase: string | null = null;
      let nextOrderEditPath: string | null = null;
      if (submitApplyMode === 'one_time') {
        nextOrderLockPhrase = formatConsumablesOrderLockCopyPhrase(orderAfterSubmit);
        if (orderAfterSubmit?.id != null) {
          nextOrderEditPath = `${PANTRY_ORDERS_PATH}/${orderAfterSubmit.id}/edit`;
        }
      }

      const confirmedLineItems: ConsumablesPickerConfirmedLineItem[] = lines.map(line => {
        let option: ConsumablesProductPickerOption | undefined;
        for (const g of groups) {
          option = (g.options || []).find(o => o.product_id === line.productId);
          if (option) break;
        }
        const unitCents = option != null ? Number(option.unit_amount_cents) : NaN;
        const safeUnit = Number.isFinite(unitCents) ? unitCents : 0;
        return {
          productId: line.productId,
          title: option?.title ?? `Product ${line.productId}`,
          quantity: line.quantity,
          lineTotalCents: Math.round(safeUnit * line.quantity),
          imageKey: option?.image_key ?? null
        };
      });
      const totalCents = confirmedLineItems.reduce((sum, item) => sum + item.lineTotalCents, 0);
      const budgetCheckOrderId = orderAfterSubmit?.id ?? resolvedOrderId ?? null;
      setConfirmedSnapshot({
        applyMode: submitApplyMode,
        lineItems: confirmedLineItems,
        totalCents,
        budgetCheckOrderId
      });
      setViewPhase('pickerExiting');
      const nextConfirmDetails = {
        applyMode: submitApplyMode,
        nextOrderLockPhrase,
        nextOrderEditPath
      };
      setConfirmDetails(nextConfirmDetails);
      onConfirm?.(nextConfirmDetails);
      onRestoreConfirmDetails?.(nextConfirmDetails);

      if (persistDebounceTimerRef.current !== null) {
        window.clearTimeout(persistDebounceTimerRef.current);
        persistDebounceTimerRef.current = null;
      }

      const terminalConfirmedSnapshot: ConfirmedSnapshot = {
        applyMode: submitApplyMode,
        lineItems: confirmedLineItems,
        totalCents,
        budgetCheckOrderId
      };
      const terminalDraftState: ConsumablesPickerDraftPayload = {
        selectedByGroup,
        quantities,
        applyMode: submitApplyMode,
        viewPhase: 'confirmed',
        confirmedSnapshot: terminalConfirmedSnapshot,
        confirmDetails: nextConfirmDetails
      };

      try {
        await wrapRequest(
          saveConsumablesPickerDraft(conversationId, messageId, payloadFingerprint, terminalDraftState),
          {
            showSuccessMessage: false,
            showErrorMessage: false
          }
        );
      } catch {
        // Non-fatal: order is already saved; draft persistence is best-effort for reload UX.
      }

      setBudgetError(null);
      setBudgetNoConfig(false);
    } catch (error) {
      const message = isApiErrorResponse(error) ? error.message : undefined;
      showSnackbar({ message, negative: true });
    } finally {
      setSubmitting(false);
    }
  };

  const addButtonLabel = applyMode === 'recurring' ? 'Add to orders' : 'Add to next order';
  const selectedLineItems = groups.flatMap(group =>
    (selectedByGroup[group.id] || []).flatMap(productId => {
      const opt = (group.options || []).find(o => o.product_id === productId);
      if (!opt) return [];
      const quantity = quantities[qtyKey(group.id, productId)] ?? 1;
      return [
        {
          id: `${group.id}:${productId}`,
          title: opt.title,
          quantity,
          amountCents: opt.unit_amount_cents * quantity
        }
      ];
    })
  );
  const visibleLineItems = selectedLineItems.length > 3 ? selectedLineItems.slice(0, 2) : selectedLineItems.slice(0, 3);
  const remainingLineItemCount = selectedLineItems.length > 3 ? selectedLineItems.length - visibleLineItems.length : 0;

  if (!isHydrated) {
    return null;
  }

  if (viewPhase === 'confirmed' && confirmedSnapshot) {
    return (
      <Layout
        __className="AIAssistantConsumablesPicker"
        direction="column"
        gap={16}
        marginTop={12}
        width="100%"
        minWidth={0}
      >
        <div className="AIAssistantConsumablesPicker-confirmedEnter">
          <AIAssistantConsumablesPickerConfirmed
            applyMode={confirmedSnapshot.applyMode}
            lineItems={confirmedSnapshot.lineItems}
            totalCents={confirmedSnapshot.totalCents}
            budgetCheckOrderId={confirmedSnapshot.budgetCheckOrderId ?? payload.upcoming_order_id ?? null}
          />
        </div>
      </Layout>
    );
  }

  if (groups.length === 0) {
    return null;
  }

  return (
    <Layout
      __className={classNames('AIAssistantConsumablesPicker', {
        'AIAssistantConsumablesPicker-pickerStage--exiting': viewPhase === 'pickerExiting'
      })}
      direction="column"
      gap={16}
      marginTop={12}
      width="100%"
      minWidth={0}
    >
      {groups.map(group => (
        <Layout
          key={group.id}
          __className="AIAssistantConsumablesPicker-group"
          direction="column"
          gap={10}
          width="100%"
        >
          <Layout __className="AIAssistantConsumablesPicker-header" direction="column" gap={4}>
            <Text size="body-sm" semibold>
              {group.label}
            </Text>
            {(group.min_select ?? 1) > 0 ? (
              <Text size="body-xs" color="gray-600">
                {(() => {
                  const min = group.min_select ?? 1;
                  if (min > 1) {
                    return `Choose at least ${min} products below (you can select as many as you need).`;
                  }
                  return 'Select any products below — you can choose more than one.';
                })()}
              </Text>
            ) : null}
          </Layout>
          <div className="AIAssistantConsumablesPicker-carouselFrame">
            {(() => {
              const scrollState = carouselScrollStateByGroup[group.id] || EMPTY_CAROUSEL_SCROLL_STATE;

              return (
                <>
                  {scrollState.canScrollLeft ? (
                    <div className="AIAssistantConsumablesPicker-carouselArrow is-left">
                      <CarouselArrow direction="left" onClick={() => handleCarouselArrowClick(group.id, 'left')} />
                    </div>
                  ) : null}
                  <div
                    className="AIAssistantConsumablesPicker-carousel"
                    ref={element => {
                      carouselRefs.current[group.id] = element;
                    }}
                    onScroll={() => updateCarouselScrollState(group.id)}
                  >
                    {(group.options || []).map(opt => {
                      const selectedGroupProductIds = selectedByGroup[group.id] || [];
                      const selected = selectedGroupProductIds.includes(opt.product_id);
                      const q = quantities[qtyKey(group.id, opt.product_id)] ?? 0;
                      const updateProductQuantity = (quantity: number) => {
                        const next = Math.min(MAX_QTY, Math.max(0, Math.floor(quantity)));
                        setQuantities(prev => ({ ...prev, [qtyKey(group.id, opt.product_id)]: next }));
                        setSelectedByGroup(prev => {
                          const existing = prev[group.id] || [];
                          const productIsSelected = existing.includes(opt.product_id);
                          if (next === 0) {
                            return {
                              ...prev,
                              [group.id]: existing.filter(productId => productId !== opt.product_id)
                            };
                          }
                          if (productIsSelected) {
                            return prev;
                          }
                          return {
                            ...prev,
                            [group.id]: [...existing, opt.product_id]
                          };
                        });
                      };

                      return (
                        <AIAssistantConsumablesPickerProductCard
                          key={opt.product_id}
                          option={opt}
                          selected={selected}
                          quantity={q}
                          maxQty={MAX_QTY}
                          currentOrderQuantity={orderQtyByProductId[opt.product_id] ?? 0}
                          currentSubscriptionQuantity={subscriptionQtyByProductId[opt.product_id] ?? 0}
                          onSelect={() => {
                            if (selected) {
                              updateProductQuantity(0);
                              return;
                            }
                            updateProductQuantity(Math.max(1, q));
                          }}
                          onQuantityDelta={delta => {
                            updateProductQuantity(q + delta);
                          }}
                          onQuantityInput={quantity => {
                            updateProductQuantity(quantity);
                          }}
                        />
                      );
                    })}
                  </div>
                  {scrollState.canScrollRight ? (
                    <div className="AIAssistantConsumablesPicker-carouselArrow is-right">
                      <CarouselArrow direction="right" onClick={() => handleCarouselArrowClick(group.id, 'right')} />
                    </div>
                  ) : null}
                </>
              );
            })()}
          </div>
        </Layout>
      ))}
      {hasAnySelection ? (
        <Layout
          __className={classNames('AIAssistantConsumablesPicker-footer', 'AIAssistantConsumablesPicker-footer--enter', {
            'is-overBudget': budgetError != null
          })}
          borderRadius={16}
          direction="column"
          width="100%"
          minWidth={0}
        >
          {isMobile ? (
            <Layout direction="column" width="100%" minWidth={0}>
              <Layout
                __className={classNames(
                  'AIAssistantConsumablesPicker-footerActionRow',
                  'AIAssistantConsumablesPicker-footerMobileTop'
                )}
                justify="space-between"
                align="center"
                gap={12}
                width="100%"
              >
                <Layout direction="row" gap={8} align="center" flexShrink={1} minWidth={0}>
                  <ToggleControl
                    checked={applyMode === 'recurring'}
                    onChange={checked => setApplyMode(checked ? 'recurring' : 'one_time')}
                    size="sm"
                  />
                  <Text size="body-sm" color="gray-900">
                    Recurring
                  </Text>
                </Layout>
                <span className="AIAssistantConsumablesPicker-footerTotal">
                  <Text size="body-sm" color={budgetError != null ? 'red-900' : 'gray-900'} semibold wrap={false}>
                    {formatUsd(pickerSubtotalCents / 100)}
                  </Text>
                </span>
              </Layout>
              <Layout __className="AIAssistantConsumablesPicker-footerCtaRow" width="100%" minWidth={0}>
                <PillButton
                  type="purpleGradient"
                  size="md"
                  text={addButtonLabel}
                  fullWidth
                  disabled={!canAddToOrder || submitting}
                  showSpinner={submitting}
                  onClick={() => {
                    void handleAddToOrder();
                  }}
                />
              </Layout>
            </Layout>
          ) : (
            <Layout
              __className="AIAssistantConsumablesPicker-footerActionRow"
              justify="space-between"
              align="center"
              gap={12}
            >
              <Layout direction="row" gap={8} align="center">
                <ToggleControl
                  checked={applyMode === 'recurring'}
                  onChange={checked => setApplyMode(checked ? 'recurring' : 'one_time')}
                  size="sm"
                />
                <Text size="body-sm" color="gray-900">
                  Recurring
                </Text>
              </Layout>
              <Layout direction="row" align="center" justify="flex-end" gap={12}>
                <span className="AIAssistantConsumablesPicker-footerTotal">
                  <Text size="body-sm" color={budgetError != null ? 'red-900' : 'gray-900'} semibold>
                    {formatUsd(pickerSubtotalCents / 100)}
                  </Text>
                </span>
                <PillButton
                  type="purpleGradient"
                  size="md"
                  text={addButtonLabel}
                  disabled={!canAddToOrder || submitting}
                  showSpinner={submitting}
                  onClick={() => {
                    void handleAddToOrder();
                  }}
                />
              </Layout>
            </Layout>
          )}
          {budgetError != null ? (
            <Layout
              __className={classNames(
                'AIAssistantConsumablesPicker-footerError',
                'AIAssistantConsumablesPicker-footerPostCta'
              )}
              direction="row"
              align="center"
              gap={8}
            >
              <span className="AIAssistantConsumablesPicker-footerErrorDot" />
              <Text size="body-xs" color="red-900">
                This order exceeds your team budget — remove an item to continue
              </Text>
            </Layout>
          ) : null}
          <Layout __className="AIAssistantConsumablesPicker-footerLineItems" direction="column">
            {visibleLineItems.map(item => (
              <Layout
                key={item.id}
                __className="AIAssistantConsumablesPicker-footerLineItem"
                direction="row"
                justify="space-between"
                align="center"
                gap={12}
              >
                <Text size="body-xs" color="gray-700" maxLines={1}>
                  {item.title} × {item.quantity}
                </Text>
                <Text size="body-xs" color="gray-700" wrap={false}>
                  {formatUsd(item.amountCents / 100)}
                </Text>
              </Layout>
            ))}
            {remainingLineItemCount > 0 ? (
              <Layout __className="AIAssistantConsumablesPicker-footerLineItem">
                <Text size="body-xs" color="gray-500">
                  and {remainingLineItemCount} more items
                </Text>
              </Layout>
            ) : null}
          </Layout>
          {resolvedOrderId == null ? (
            <Text size="body-xs" color="gray-600">
              There is no open pantry order to add to right now. Open the pantry to manage deliveries.
            </Text>
          ) : null}
          {payload.order_edit_url ? (
            <Layout __className="AIAssistantConsumablesPicker-footerLink">
              <Link href={payload.order_edit_url} target="_blank" size="link-md" semibold={false}>
                Open pantry order
              </Link>
            </Layout>
          ) : null}
        </Layout>
      ) : null}
    </Layout>
  );
};

export default AIAssistantConsumablesProductPicker;
